import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wand2 } from 'lucide-react'
import { useToast, Button, Card, Alert } from '@orientamos/ui'
import { useIntakeForm } from '../hooks/useIntakeForm'
import { SectionNav } from './SectionNav'
import { AIImportModal } from './AIImportModal'
import {
  Step1ContractType,
  Step2Property,
  Step3Tenant,
  Step4Guarantors,
  Step5Owners,
  Step6Terms,
} from './steps'
import type { IntakeFormData } from '@orientamos/shared'
import type { AIExtractionResult } from '@lib/api'

interface IntakeFormProps {
  initialData?: Partial<IntakeFormData>
  intakeId?: string
}

export function IntakeForm({ initialData, intakeId }: IntakeFormProps) {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [showAIImport, setShowAIImport] = useState(false)

  const { form, isSubmitting, isSaving, sectionIds, submitForm } = useIntakeForm({
    initialData,
    intakeId,
  })

  const handleSubmit = async () => {
    const result = await submitForm()
    if (result.success) {
      addToast({
        variant: 'success',
        message: 'La orden de contrato ha sido guardada exitosamente',
      })
      navigate(`/intakes/${result.id}`)
    } else {
      if (result.invalidSection) {
        const el = document.getElementById(result.invalidSection)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }
      addToast({
        variant: 'error',
        message: result.error || 'Ocurrio un error al guardar la orden',
      })
    }
  }

  const handleApplyAIFields = useCallback(
    (fields: AIExtractionResult['fields']) => {
      if (fields.contractType) {
        form.setValue('contractType', fields.contractType)
      }

      if (fields.property) {
        if (fields.property.address) {
          form.setValue('property.address', fields.property.address)
        }
        if (fields.property.city) {
          form.setValue('property.city', fields.property.city)
        }
        if (fields.property.stratum !== null && fields.property.stratum !== undefined) {
          form.setValue('property.stratum', fields.property.stratum)
        }
        if (fields.property.area !== null && fields.property.area !== undefined) {
          form.setValue('property.area', fields.property.area)
        }
        if (fields.property.propertyType) {
          const typeMap: Record<string, string> = {
            apartamento: 'apartamento',
            casa: 'casa',
            local: 'local',
            oficina: 'oficina',
            bodega: 'bodega',
            lote: 'lote',
            parqueadero: 'parqueadero',
          }
          const mappedType = typeMap[fields.property.propertyType.toLowerCase()]
          if (mappedType) {
            form.setValue(
              'property.propertyType',
              mappedType as IntakeFormData['property']['propertyType']
            )
          }
        }
      }

      if (fields.tenant) {
        if (fields.tenant.firstName) form.setValue('tenant.firstName', fields.tenant.firstName)
        if (fields.tenant.lastName) form.setValue('tenant.lastName', fields.tenant.lastName)
        if (fields.tenant.idNumber) form.setValue('tenant.idNumber', fields.tenant.idNumber)
        if (fields.tenant.phone) form.setValue('tenant.phone', fields.tenant.phone)
        if (fields.tenant.email) form.setValue('tenant.email', fields.tenant.email)
        if (fields.tenant.address) form.setValue('tenant.address', fields.tenant.address)
        if (fields.tenant.city) form.setValue('tenant.city', fields.tenant.city)
      }

      if (fields.owner) {
        if (fields.owner.firstName) form.setValue('owners.0.firstName', fields.owner.firstName)
        if (fields.owner.lastName) form.setValue('owners.0.lastName', fields.owner.lastName)
        if (fields.owner.idNumber) form.setValue('owners.0.idNumber', fields.owner.idNumber)
        if (fields.owner.phone) form.setValue('owners.0.phone', fields.owner.phone)
        if (fields.owner.email) form.setValue('owners.0.email', fields.owner.email)
        if (fields.owner.address) form.setValue('owners.0.address', fields.owner.address)
        if (fields.owner.city) form.setValue('owners.0.city', fields.owner.city)
      }

      if (fields.terms) {
        if (fields.terms.monthlyRent !== null && fields.terms.monthlyRent !== undefined) {
          form.setValue('terms.monthlyRent', fields.terms.monthlyRent)
        }
        if (
          fields.terms.administrationFee !== null &&
          fields.terms.administrationFee !== undefined
        ) {
          form.setValue('terms.administrationFee', fields.terms.administrationFee)
        }
        if (fields.terms.startDate) form.setValue('terms.startDate', fields.terms.startDate)
        if (fields.terms.endDate) form.setValue('terms.endDate', fields.terms.endDate)
        if (fields.terms.durationMonths !== null && fields.terms.durationMonths !== undefined) {
          form.setValue('terms.durationMonths', fields.terms.durationMonths)
        }
      }

      addToast({ variant: 'success', message: 'Campos importados exitosamente' })
    },
    [form, addToast]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formAny = form as any

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        {/* AI Import Button */}
        <div className="mb-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowAIImport(true)}
            className="gap-2"
          >
            <Wand2 className="h-4 w-4" />
            Importar con IA
          </Button>
        </div>

        <div className="flex gap-8">
          {/* Sidebar nav (desktop only) */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <SectionNav sectionIds={sectionIds} />
          </div>

          {/* Main content: all sections stacked */}
          <div className="flex-1 min-w-0">
            {/* Mobile nav */}
            <div className="lg:hidden mb-4">
              <SectionNav sectionIds={sectionIds} />
            </div>

            <div className="space-y-8">
              <Card id="contract-type" className="scroll-mt-24">
                <Step1ContractType form={formAny} />
              </Card>

              <Card id="property" className="scroll-mt-24">
                <Step2Property form={formAny} />
              </Card>

              <Card id="tenant" className="scroll-mt-24">
                <Step3Tenant form={formAny} />
              </Card>

              <Card id="guarantors" className="scroll-mt-24">
                <Step4Guarantors form={formAny} />
              </Card>

              <Card id="owners" className="scroll-mt-24">
                <Step5Owners form={formAny} />
              </Card>

              <Card id="terms" className="scroll-mt-24">
                <Step6Terms form={formAny} />
              </Card>

              {/* Submit footer */}
              <Card>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isSaving && (
                      <span className="text-sm text-neutral-500 animate-pulse">Guardando...</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Guardar Orden
                  </Button>
                </div>
                <Alert variant="info" className="mt-4">
                  Al guardar, la orden quedara en estado borrador hasta que sea aprobada por un
                  supervisor.
                </Alert>
              </Card>
            </div>
          </div>
        </div>
      </form>

      <AIImportModal
        open={showAIImport}
        onClose={() => setShowAIImport(false)}
        onApply={handleApplyAIFields}
      />
    </>
  )
}
